/**
 * COSO Compliance Static Data
 * 
 * Contains hardcoded COSO framework structure data:
 * - 5 Core Components with styling
 * - 17 Principles with descriptions and navigation links
 * 
 * Note: The narrative content for each principle comes from the API,
 * not from this file. This file only contains static framework data.
 */

// ========== TYPE DEFINITIONS ==========

export interface COSOPrinciple {
  id: string;
  number: number;
  title: string;
  description: string;
  componentId: string;
  navigationLink?: {
    label: string;
    path: string;
    state?: Record<string, unknown>;
  };
}

export interface COSOComponent {
  id: string;
  name: string;
  bgColor: string;
  principleIds: number[];
}

// ========== STATIC DATA ==========

/**
 * 5 COSO Components with their styling
 * Colors are based on the design specification
 */
export const COSO_COMPONENTS: COSOComponent[] = [
  {
    id: 'control-environment',
    name: 'Control Environment',
    bgColor: '#E8E4F3', // Light purple/lavender
    principleIds: [1, 2, 3, 4, 5],
  },
  {
    id: 'risk-assessment',
    name: 'Risk Assessment',
    bgColor: '#E0F2FE', // Light blue
    principleIds: [6, 7, 8, 9],
  },
  {
    id: 'control-activities',
    name: 'Control Activities',
    bgColor: '#DBEAFE', // Light blue variant
    principleIds: [10, 11, 12],
  },
  {
    id: 'information-communication',
    name: 'Information and Communication',
    bgColor: '#D1FAE5', // Light green/mint
    principleIds: [13, 14, 15],
  },
  {
    id: 'monitoring-activities',
    name: 'Monitoring Activities',
    bgColor: '#CFFAFE', // Light cyan/aqua
    principleIds: [16, 17],
  },
];

/**
 * 17 COSO Principles
 * Navigation links are defined based on requirements:
 * - Principle 6: Objectives Setting → /risk-assessment (EX4 Tab 2, universal view)
 * - Principle 7: Objectives Setting → /risk-assessment (EX4 Tab 2, universal view)
 * - Principle 8: All fraud risks → /risk-assessment (EX4 Tab 3, universal view)
 * - Principle 9: Process Owner Responses → /certification?tab=checklist (EX7 Change Indicators)
 * - Principle 10: Control Activities → /control-activities
 * - Principle 17: All active GAPs → /gaps
 * 
 * Note: Content/narrative is NOT included here - it comes from the API
 */
export const COSO_PRINCIPLES: COSOPrinciple[] = [
  // Control Environment (1-5)
  {
    id: 'principle-1',
    number: 1,
    title: 'Integrity and Ethical Values',
    description: 'Demonstrates commitment to integrity and ethical values',
    componentId: 'control-environment',
  },
  {
    id: 'principle-2',
    number: 2,
    title: 'Oversight Responsibility',
    description: 'Exercises oversight responsibility',
    componentId: 'control-environment',
  },
  {
    id: 'principle-3',
    number: 3,
    title: 'Structure, Authority, and Responsibility',
    description: 'Establishes structure, authority, and responsibility',
    componentId: 'control-environment',
  },
  {
    id: 'principle-4',
    number: 4,
    title: 'Commitment to Competence',
    description: 'Demonstrates commitment to competence',
    componentId: 'control-environment',
  },
  {
    id: 'principle-5',
    number: 5,
    title: 'Accountability',
    description: 'Enforces accountability',
    componentId: 'control-environment',
  },
  
  // Risk Assessment (6-9)
  {
    id: 'principle-6',
    number: 6,
    title: 'Suitable Objectives',
    description: 'Specifies suitable objectives',
    componentId: 'risk-assessment',
    navigationLink: {
      label: 'All objectives',
      path: '/risk-assessment-total/objectives',
    },
  },
  {
    id: 'principle-7',
    number: 7,
    title: 'Identifies and Analyzes Risk',
    description: 'Identifies and analyses risk',
    componentId: 'risk-assessment',
    navigationLink: {
      label: 'All risks',
      path: '/risk-assessment-total/fraud-risks',
    },
  },
  {
    id: 'principle-8',
    number: 8,
    title: 'Fraud Risk',
    description: 'Assesses fraud risk',
    componentId: 'risk-assessment',
    navigationLink: {
      label: 'All fraud risks',
      path: '/risk-assessment',
    },
  },
  {
    id: 'principle-9',
    number: 9,
    title: 'Significant Change',
    description: 'Identifies and analyses significant change',
    componentId: 'risk-assessment',
    navigationLink: {
      label: 'Change Indicators Checklist',
      path: '/certification?tab=checklist',
    },
  },
  
  // Control Activities (10-12)
  {
    id: 'principle-10',
    number: 10,
    title: 'Control Activities',
    description: 'Selects and develops control activities',
    componentId: 'control-activities',
    navigationLink: {
      label: 'Control Activities',
      path: '/control-activities',
    },
  },
  {
    id: 'principle-11',
    number: 11,
    title: 'General Controls over Technology',
    description: 'Selects and develops general controls over technology',
    componentId: 'control-activities',
  },
  {
    id: 'principle-12',
    number: 12,
    title: 'Policies and Procedures',
    description: 'Deploys through policies and procedures',
    componentId: 'control-activities',
  },
  
  // Information and Communication (13-15)
  {
    id: 'principle-13',
    number: 13,
    title: 'Relevant Information',
    description: 'Uses relevant information',
    componentId: 'information-communication',
  },
  {
    id: 'principle-14',
    number: 14,
    title: 'Internal Communication',
    description: 'Communicates internally',
    componentId: 'information-communication',
  },
  {
    id: 'principle-15',
    number: 15,
    title: 'External Communication',
    description: 'Communicates externally',
    componentId: 'information-communication',
  },
  
  // Monitoring Activities (16-17)
  {
    id: 'principle-16',
    number: 16,
    title: 'Ongoing and/or Separate Evaluations',
    description: 'Conducts ongoing and/or separate evaluations',
    componentId: 'monitoring-activities',
  },
  {
    id: 'principle-17',
    number: 17,
    title: 'Deficiencies Communication',
    description: 'Evaluates and communicates deficiencies',
    componentId: 'monitoring-activities',
    navigationLink: {
      label: 'All active GAPs',
      path: '/gaps',
      state: { status: 'Open' },
    },
  },
];

// ========== HELPER FUNCTIONS ==========

/**
 * Get principles by component ID
 */
export const getPrinciplesByComponent = (componentId: string): COSOPrinciple[] => {
  return COSO_PRINCIPLES.filter(p => p.componentId === componentId);
};

/**
 * Get principle by ID
 */
export const getPrincipleById = (principleId: string): COSOPrinciple | undefined => {
  return COSO_PRINCIPLES.find(p => p.id === principleId);
};

/**
 * Get principle by number
 */
export const getPrincipleByNumber = (number: number): COSOPrinciple | undefined => {
  return COSO_PRINCIPLES.find(p => p.number === number);
};

/**
 * Get component by ID
 */
export const getComponentById = (componentId: string): COSOComponent | undefined => {
  return COSO_COMPONENTS.find(c => c.id === componentId);
};

